import { defineComponent as _defineComponent } from 'vue'
import {ProductType} from '@/commons/enums/ProductType';
import SimulationOfferListing from '@/components/calculator/results/offers/SimulationOfferListing.vue';
import UserInputPanel from '@/components/calculator/results/userInputPanel/UserInputPanel.vue';
import {SimulationResultConfig} from '@/components/calculator/results/SimulationResultConfig';
import Client from '@/models/Client';
import {useSimulationStore} from '@/components/calculator/services/SimulationStore';
import Vue, {computed, defineComponent, getCurrentInstance, onMounted, PropType, ref, Ref} from 'vue';
import SimulationToolbar from '@/components/calculator/results/toolbar/SimulationToolbar.vue';
import {useDealForm} from '@/components/calculator/form/useSimulationForm';
import {useOffersTable} from '@/components/calculator/results/offers/useOffersTable';
import useOfferSorting from '@/components/calculator/results/OfferSorting';
import HorizontalNavigation from '@/components/calculator/results/HorizontalNavigation.vue';
import {useZoom} from '@/components/calculator/results/useZoom';
import SimulationApi from '@/modules/calculator/services/SimulationApi';
import SendOrDownloadOffersModal from '@/components/calculator/printout/SendOrDownloadOffersModal.vue';
import {OfferDataTableElementKey} from '@/components/calculator/results/offers/OfferTableElement';
import {usePrintoutSettings} from '@/components/calculator/printout/usePrintoutSettings';
import SimulationDifferentValuesInfo from '@/components/calculator/results/SimulationDifferentValuesInfo.vue';
import SimulationToolbarSort from '@/components/calculator/results/toolbar/SimulationToolbarSort.vue';
import {CalculatorVersion, useCalculatorVersion} from '@/components/calculator/useCalculatorVersion';
import { I18NGetter } from '@/services/enumTranslator/I18NGetter';
import DealsApi from '@/modules/deals/services/DealsApi';
import SimulationDealPicker from '@/components/calculator/results/SimulationDealPicker.vue';
import AddOffersToDealModal from '@/components/calculator/results/AddOffersToDealModal.vue';
import { useInstance } from '@/composables/useInstance';
import {usePrintout} from '@/components/calculator/printout/usePrintout';
import {useVhHack} from '@/composables/useVhHack';
import {Offer} from '@/models/simulation/Simulation';
import processProxy from '@/env/processProxy';


export default /*#__PURE__*/_defineComponent({
  __name: 'SimulationResults',
  props: {
    simulationResultConfig: null
  },
  setup(__props: any) {

const props = __props as {
  simulationResultConfig: SimulationResultConfig;
}



const store = useSimulationStore();
const instance = getCurrentInstance();
const {$modalService, $user, $snackbarService, $goto, $errorHandler, $i18n, } = useInstance();
const printout = usePrintout();
const {fetchDeal,} = useDealForm();
const {setDataTableElementByType, renderedOfferTableElements,} = useOffersTable();
const {
  simulationOfferListingZoom,
} = useZoom(store.productType!, instance?.proxy.$vuetify.breakpoint.mdAndDown);
const {version,} = useCalculatorVersion();

onMounted(() => {
  fetchDeal();
  setDataTableElementByType(store.productType!);
  store.saveInitialUserInput();
  store.isShownAvailableProducts = true;
});

const unselectOffers = async function() {
  try {
    const response = await $modalService.openConfirmModal($i18n.useSimulationResult.UNSELECT_ALL_OFFERS_MODAL_TEXT)
    if (response) {
      await store.unselectedAllOffers()
    }
  } catch (e) {
    $errorHandler(e, $i18n.useSimulationResult.UNSELECT_ALL_OFFERS_ERROR);
  }
}

useVhHack();

const isOldView = computed(() => version.value === CalculatorVersion.V1);

const iframeUrl = computed(() => {
  let prefix: string = '';
  if (store.productType === ProductType.CASH) {
    prefix = 'cash/';
  }
  return `${processProxy.VUE_APP_IFRAME_URL}finrate-iframe/#/${prefix}simulation/${store.simulationId}`;
});

const hasSelectedOffers = computed<boolean>(() => store.selectedOffersCount > 0);

const printOffers = async() => {
  if (hasSelectedOffers.value) {
    await printout.downloadSimulationPrintout();
  } else {
    $snackbarService.openWarningSnackbar(I18NGetter().useSimulationResult
      .PRINT_PREVIEW_OFFERS_WARNING_SNACKBAR);
  }
};

const openPrintoutPreview = () => {
  if (hasSelectedOffers.value) {
    printout.previewSimulationPrintout();
  } else {
    $snackbarService.openWarningSnackbar(I18NGetter().useSimulationResult
      .PRINT_PREVIEW_OFFERS_WARNING_SNACKBAR);
  }
};

const createApplications = async() => {
  if (hasSelectedOffers.value) {
    try {
      const selectedOffers = store.filteredOffers
        .filter(offer => store.userInput?.selectedOffers.includes(offer.info.id)) ?? [];
      if (store.deal) {
        const response: boolean | Offer[] | undefined = await $modalService.open(AddOffersToDealModal, {
          selectedOffers,
          user: $user.value,
        }, { fullscreen: undefined, maxWidth: 800, persistent: true, });
        if (response) {
          const offersToSend: Offer[] = Array.isArray(response) ? response : selectedOffers;
          await DealsApi.updateDeal({
            id: store.deal.id,
            simulationId: store.simulationId!,
            loanAmount: store.userInput?.loanAmount || store.deal.loanAmount,
          });
          await SimulationApi.addOffersToDeal(
            store.deal?.id, store.simulationId!, store.userInput!, offersToSend
          );
          $snackbarService.openSuccessSnackbar(I18NGetter().useSimulationResult
            .CREATE_APPLICATION_OPEN_SUCCESS_SNACKBAR);
          $goto.deal(store.deal?.id);
        }
      } else {
        await $modalService.open<Client>(SimulationDealPicker, {
          productType: store.productType,
          simulationId: store.simulationId,
          userInput: store.userInput,
          offers: selectedOffers,
        });
      }
    } catch (e) {
      $errorHandler(e, I18NGetter().useSimulationResult
        .CREATE_APPLICATION_ERROR_HANDLER);
    }
  } else {
    $snackbarService.openWarningSnackbar(I18NGetter().useSimulationResult
      .CREATE_APPLICATION_OPEN_WARNING_SNACKBAR);
  }
};
const openSendOrDownloadModal = async() => {
  const selectedOffers: Offer[] = store.simulation?.offers.filter(offer => {
    return store.userInput?.selectedOffers.includes(offer.info?.id);
  }) ?? [];
  if (hasSelectedOffers.value) {
    await $modalService.open(SendOrDownloadOffersModal,
      {
        productType: store.productType!,
        selectedOffers,
        userInput: store.userInput,
      });
  } else {
    $snackbarService.openWarningSnackbar(I18NGetter().useSimulationResult
      .PRINT_PREVIEW_OFFERS_WARNING_SNACKBAR);
  }
};

return { __sfc: true,props, store, instance, $modalService, $user, $snackbarService, $goto, $errorHandler, $i18n, printout, fetchDeal, setDataTableElementByType, renderedOfferTableElements, simulationOfferListingZoom, version, unselectOffers, isOldView, iframeUrl, hasSelectedOffers, printOffers, openPrintoutPreview, createApplications, openSendOrDownloadModal, SimulationOfferListing, UserInputPanel, SimulationToolbar, HorizontalNavigation, SimulationDifferentValuesInfo, SimulationToolbarSort, I18NGetter }
}

})